<script lang="ts" setup>
import { fmtMSS } from '~/const/conversion'
import { getPersonBySlug } from '~/queries/composer'
const { slug } = useResourceSlug()
const { locale } = useI18n()
const { data, pending, error } = await useAsyncData(
  `/composer/${slug}/works`,
  () =>
    execQuery(() => getPersonBySlug('works'), {
      slug,
    })
)

const composer = computed(() => {
  if (pending.value || error.value) return null
  return data.value.data.personBySlug
})

const title = computed(() => {
  return `Oeuvres par date - ${composer.value?.firstName} ${composer.value?.lastName}`
})

const description = computed(() => {
  return `La liste des oeuvres par date du compositeur ${composer.value?.firstName} ${composer.value?.lastName}`
})

useHead({
  title,
  meta: [
    {
      name: 'description',
      content: description,
    },
  ],
})

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: composer.value?.photo?.getUrl,
  twitterCard: 'summary',
})
function formatWork(work) {
  if (!work) return null
  let start = work.publishingStartDate
  let end = work.publishingEndDate
  let title = work.title
  let subtitle = work.descriptionFr
  let subtitleEn = work.descriptionEn
  if (work.duration) {
    subtitle += `${subtitle ? ', ' : ''}${fmtMSS(work.duration)}`
    subtitleEn += `${subtitleEn ? ', ' : ''}${fmtMSS(work.duration)}`
  }
  if (start) {
    start = new Date(start).getFullYear()
    title += ` (${start}`
    if (end) {
      end = new Date(end).getFullYear()
      if (end !== start) {
        title += `-${end}`
      }
    }
    title += ')'
  }
  return {
    ...work,
    type: 'work',
    title,
    subtitle,
    subtitleEn,
  }
}
// Group by Year
const worksByYear: WorksByYear[] = []
const sortedWorks = composer?.value.works
  ?.filter(({ publishingStartDate }) => publishingStartDate)
  .sort((a, b) => b?.publishingStartDate?.localeCompare(a?.publishingStartDate))
sortedWorks.map((work: Work) => {
  const year = new Date(work.publishingStartDate).getFullYear().toString()
  const yearIndex = worksByYear.findIndex((group) => group.year === year)

  if (yearIndex === -1) {
    worksByYear.push({ year, works: [formatWork(work)] })
  } else {
    worksByYear[yearIndex].works.push(formatWork(work))
  }
  return work
})
</script>
<template>
  <DetailComposer
    :resume="
      locale === 'fr'
        ? composer?.resume
        : composer?.resumeEn ?? composer?.resume
    "
    :composer="composer"
    :active-outline-id="'list_of_work_by_date'"
  >
    <template #typeIcon>
      <div
        v-if="composer?.photo?.getUrl"
        class="hidden h-40 w-40 shrink-0 items-center justify-center bg-white sm:flex lg:h-60 lg:w-60"
      >
        <div class="relative">
          <img
            :src="composer?.photo?.getUrl"
            :alt="
              'biography of ' + composer?.firstName + ' ' + composer?.lastName
            "
            class="h-full w-full object-cover sm:p-0"
          />
          <span
            v-if="composer?.copyrights"
            class="absolute bottom-0 right-1 text-white"
            >© {{ composer?.copyrights }}</span
          >
        </div>
      </div>
    </template>
    <template #prose>
      <div>
        <div>
          <div v-for="item in worksByYear" :key="item.id" class="md:mt-4">
            <span
              class="font-semibold text-primary-400 first-letter:uppercase"
              >{{ item.year }}</span
            >
            <hr class="my-1 border-t border-gray-300" />

            <div>
              <ListResource
                v-for="work in item.works"
                :key="work"
                :resource="work"
                :hide-icon="true"
                :minified="true"
              />
            </div>
          </div>
          <div
            v-if="
              composer?.works?.filter(
                ({ publishingStartDate }) => !publishingStartDate
              ).length > 0
            "
            class="mt-6"
          >
            <span
              class="font-semibold text-primary-400 first-letter:uppercase"
              v-if="locale === 'fr'"
              >Date non spécifiée</span
            >
            <span
              class="font-semibold text-primary-400 first-letter:uppercase"
              v-else
              >Unspecified date</span
            >

            <hr class="my-1 border-t border-gray-300" />
          </div>
          <div>
            <ListResource
              v-for="work in composer?.works
                ?.filter(({ publishingStartDate }) => !publishingStartDate)
                ?.map(formatWork)"
              :key="work"
              :resource="work"
              :hide-icon="true"
              :minified="true"
            />
          </div>
        </div>
      </div>

      <div v-html="composer?.catalogueSource" />
    </template>
  </DetailComposer>
</template>
